import strings from "global/constants/StringConstants";
import { signer } from "theme/colors";

/**
 * Screen tokens for the guest "Signer status" cards (first screen · status &
 * error states), traced 1:1 from `Signer Status Screens.dc.html`. Every design
 * value the cards need lives here so `SignerCompletionCard` carries no raw hex.
 *
 * Kept as a temporary, screen-scoped file per UI_IMPLEMENTATION_RULES — the
 * gradients/shadows below are per-status and only used by this card, so they
 * are not globalised into the `signer` palette yet.
 */

export type StatusIcon =
  | "clock"
  | "ban"
  | "check"
  | "x"
  | "personX"
  | "refresh"
  | "list"
  | "lock"
  | "delegate";

/** Footer strip drawn under a card, if any. */
export type StatusFooter = "help" | "id" | "info" | "none";

/** Extra block rendered inside the body, if any. */
export type StatusExtra = "reason" | "contact" | "notify";

export interface StatusVisual {
  /** 5px top ribbon (linear-gradient, 90deg). */
  strip: string;
  /** Circular icon badge fill (linear-gradient, 135deg). */
  circle: string;
  /** Coloured glow cast by the badge. */
  circleShadow: string;
  icon: StatusIcon;
  /** Glyph box size in px. */
  iconSize: number;
  iconStroke: number;
  /** Body bottom padding in px (the rest of the body padding is shared). */
  bottomPad: number;
  footer: StatusFooter;
  extra?: StatusExtra;
}

// ── palette pulled straight from the mock ──────────────────────────────────
const c = {
  ink: signer.ink,
  body: signer.text.meta,
  // amber (expired)
  amber1: signer.warn.main,
  amber2: "#E0A93E",
  amberGlow: "rgba(180,114,7,.32)",
  // slate (voided / no-longer)
  slate1: signer.text.meta,
  slate2: "#413B5B",
  slateGlow: "rgba(65,59,91,.3)",
  // rainbow (waiting for others)
  rain1: signer.purple.main,
  rain2: signer.gradient.coral,
  rain3: signer.gradient.amber,
  coralGlow: "rgba(239,64,58,.3)",
  // green (approved)
  green1: signer.success.main,
  green2: "#48B074",
  greenGlow: "rgba(31,138,76,.3)",
  // red (rejected / declined / verification locked)
  red1: signer.danger.main,
  red2: "#D9645C",
  redGlow: "rgba(180,55,47,.3)",
  // purple (turn / order changed / delegated)
  purple1: signer.purple.main,
  purple2: signer.purple.light,
  purpleGlow: "rgba(109,82,162,.3)",
};

const strip = (from: string, to: string) =>
  `linear-gradient(90deg,${from},${to})`;
const badge = (from: string, to: string) =>
  `linear-gradient(135deg,${from},${to})`;

/**
 * Exported so other guest-facing "status" screens (e.g. `/link-expired`) can
 * reuse the exact same amber/slate treatment instead of re-declaring the hex.
 */
export const amber: Pick<StatusVisual, "strip" | "circle" | "circleShadow"> = {
  strip: strip(c.amber1, c.amber2),
  circle: badge(c.amber1, c.amber2),
  circleShadow: `0 10px 26px ${c.amberGlow}`,
};
export const slate: Pick<StatusVisual, "strip" | "circle" | "circleShadow"> = {
  strip: strip(c.slate1, c.slate2),
  circle: badge(c.slate1, c.slate2),
  circleShadow: `0 10px 26px ${c.slateGlow}`,
};
const rainbow: Pick<StatusVisual, "strip" | "circle" | "circleShadow"> = {
  strip: `linear-gradient(90deg,${c.rain1},${c.rain2},${c.rain3})`,
  circle: `linear-gradient(135deg,${c.rain1},${c.rain2},${c.rain3})`,
  circleShadow: `0 10px 26px ${c.coralGlow}`,
};
export const green: Pick<StatusVisual, "strip" | "circle" | "circleShadow"> = {
  strip: strip(c.green1, c.green2),
  circle: badge(c.green1, c.green2),
  circleShadow: `0 10px 26px ${c.greenGlow}`,
};
export const red: Pick<StatusVisual, "strip" | "circle" | "circleShadow"> = {
  strip: strip(c.red1, c.red2),
  circle: badge(c.red1, c.red2),
  circleShadow: `0 10px 26px ${c.redGlow}`,
};
export const purple: Pick<StatusVisual, "strip" | "circle" | "circleShadow"> = {
  strip: strip(c.purple1, c.purple2),
  circle: badge(c.purple1, c.purple2),
  circleShadow: `0 10px 26px ${c.purpleGlow}`,
};

/** Per-status visual config, keyed by the same `status` string the card gets. */
export const STATUS_VISUALS: Record<string, StatusVisual> = {
  [strings.expired]: {
    ...amber,
    icon: "clock",
    iconSize: 30,
    iconStroke: 2.2,
    bottomPad: 36,
    footer: "help",
  },
  [strings.voided]: {
    ...slate,
    icon: "ban",
    iconSize: 30,
    iconStroke: 2.2,
    bottomPad: 36,
    footer: "id",
  },
  [strings.waitingForOthers]: {
    ...rainbow,
    icon: "check",
    iconSize: 32,
    iconStroke: 2.6,
    bottomPad: 34,
    footer: "info",
  },
  [strings.approved]: {
    ...green,
    icon: "check",
    iconSize: 32,
    iconStroke: 2.6,
    bottomPad: 36,
    footer: "id",
  },
  [strings.rejected]: {
    ...red,
    icon: "x",
    iconSize: 28,
    iconStroke: 2.6,
    bottomPad: 36,
    footer: "id",
    extra: "reason",
  },
  [strings.declined]: {
    ...red,
    icon: "x",
    iconSize: 28,
    iconStroke: 2.6,
    bottomPad: 36,
    footer: "id",
    extra: "reason",
  },
  [strings.nonAssociatedErrorMessage]: {
    ...slate,
    icon: "personX",
    iconSize: 30,
    iconStroke: 2,
    bottomPad: 36,
    footer: "id",
  },
  [strings.notYourTurnErrorMessageV1]: {
    ...purple,
    icon: "refresh",
    iconSize: 30,
    iconStroke: 2.2,
    bottomPad: 36,
    footer: "none",
    extra: "notify",
  },
  [strings.notYourTurnErrorMessageV2]: {
    ...purple,
    icon: "list",
    iconSize: 30,
    iconStroke: 2.2,
    bottomPad: 36,
    footer: "none",
    extra: "notify",
  },
  [strings.requestDelegatedMessage]: {
    ...purple,
    icon: "delegate",
    iconSize: 30,
    iconStroke: 2.2,
    bottomPad: 36,
    footer: "none",
  },
  [strings.verificationLocked]: {
    ...red,
    icon: "lock",
    iconSize: 30,
    iconStroke: 2,
    bottomPad: 36,
    footer: "none",
    extra: "contact",
  },
};

/** Shared chrome + typography, identical across every status card. */
export const CARD = {
  width: 472,
  shadow: "0 24px 60px rgba(0,0,0,.4)",
  logoWidth: 96,
  bodyPadX: 46,
  bodyPadTop: 40,
  circleSize: 70,
  circleGap: 28, // circle margin-top under the logo
  titleGap: 24, // title margin-top under the circle
  titleSize: "23px",
  titleColor: c.ink,
  bodyGap: 10, // body margin-top under the title
  bodySize: "13.5px",
  bodyColor: c.body,
  bodyLineHeight: 1.65,
  bodyMaxWidth: 360,
} as const;

/** Footer strip (help / id) tokens. */
export const FOOTER = {
  padY: 14,
  bg: signer.surface.subtle,
  borderTop: signer.border.light,
  text: signer.text.faint,
  fontSize: "11px",
} as const;

/** "Awaiting other signers" info strip (waiting-for-others card). */
export const INFO_STRIP = {
  bg: signer.surface.subtle,
  borderTop: signer.border.main,
  padTop: 20,
  padBottom: 22,
  gap: 12,
  chipSize: 34,
  chipBg: signer.warn.tint,
  chipBorder: signer.warn.border,
  chipIcon: signer.warn.main,
  chipIconSize: 16,
  titleColor: c.ink,
  titleSize: "13px",
  subColor: c.body,
  subSize: "11.5px",
} as const;

/** Purple "We'll notify you when it's your turn" pill (turn / order changed). */
export const NOTIFY_PILL = {
  gap: 22, // margin-top under the body
  height: 36,
  padX: 16,
  bg: signer.purple.tint,
  border: signer.purple.border,
  color: c.purple1,
  iconSize: 14,
  fontSize: "12px",
} as const;

/** "YOUR REASON" callout (rejected / declined). */
export const REASON = {
  gap: 20, // margin-top under the body
  padX: 16,
  padY: 13,
  bar: signer.danger.borderStrong,
  bg: signer.danger.tint,
  label: c.red1,
  labelSize: "11px",
  text: "#7A2E28",
  textSize: "12.5px",
} as const;

/** "Contact the requester" box (verification locked). */
export const CONTACT = {
  gap: 24,
  padX: 18,
  padY: 16,
  border: signer.border.light,
  bg: signer.surface.subtle,
  title: c.ink,
  name: signer.text.secondary,
  fontSize: "13px",
} as const;

/**
 * Signer identity verification card (`IdentityVerificationContent`). Shares
 * `CARD`'s chrome; these are the extra blocks it adds, from the IDV design
 * brief: steps list, test-mode strip, vendor placeholder frame.
 */
export const IDV = {
  sectionGap: 22, // margin-top of a block under the body copy
  stepsBg: signer.surface.subtle,
  stepsBorder: signer.border.light,
  stepsPadX: 18,
  stepsPadY: 14,
  stepNumSize: 22,
  stepNumBg: signer.purple.tint,
  stepNumColor: c.purple1,
  stepColor: c.ink,
  stepFontSize: "13px",
  noteColor: c.body,
  noteFontSize: "12px",
  testBg: signer.warn.tint,
  testBorder: signer.warn.border,
  testTitle: signer.warn.main,
  partnerBorder: signer.border.main,
  partnerBg: signer.surface.subtle,
  partnerMinHeight: 120,
  radius: "12px",
  glyphSize: 32,
} as const;
